/* One queue for theme cart mutations and BF reconciliation. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./bf-core.js'));
  else if (root.EarlyBFCore && root.EarlyBF) root.EarlyBFCart = factory(root.EarlyBFCore).mount(root, root.document);
  else if (root.EarlyBF) root.EarlyBF.deactivate();
})(typeof window === 'object' ? window : globalThis, function (core) {
  'use strict';
  function cartIdentity(token) {
    // An animation scope, never an authorization token or a cart price check.
    var hash = 2166136261, value = String(token || '');
    for (var index = 0; index < value.length; index++) hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
    return (hash >>> 0).toString(16).padStart(8, '0');
  }
  function createController(adapter) {
    var chain = Promise.resolve(), syncPromise = null, timer = null;
    var pending = 0, version = 0;
    var ledger = adapter.loadLedger() || { owned: [] };
    var state = { phase: 'idle' };
    function ownedFor(cart) { return ledger.cartToken === cart.token ? ledger.owned || [] : []; }
    function needsWork() {
      // Another tab can apply or remove our codes while this page is open.
      ledger = adapter.loadLedger() || { owned: [] };
      return adapter.isActive() || Boolean((ledger.owned || []).length || ledger.recovery);
    }
    function save(next) { ledger = next; adapter.saveLedger(ledger); }
    function publish(next) { state = Object.assign({}, next); adapter.onState(state, needsWork()); }
    function enqueue(task) { var operation = chain.then(task); chain = operation.catch(function () {}); return operation; }
    function schedule() {
      if (!needsWork()) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(function () { timer = null; synchronize(); }, adapter.debounceMs === undefined ? 80 : adapter.debounceMs);
    }
    function invalidate() {
      if (!needsWork()) return;
      version++;
      publish({ phase: 'pending' });
      schedule();
    }
    function request(url, options) {
      if (!needsWork()) return (adapter.nativeFetch || adapter.fetch)(url, options);
      pending++; version++;
      publish({ phase: 'pending' });
      return enqueue(async function () {
        var response = await adapter.fetch(url, options);
        // Hold the queue until the response body is complete. The original body
        // remains untouched for Kalles, including 422 stock/error responses.
        await response.clone().text();
        return response;
      }).finally(function () { pending--; schedule(); });
    }
    async function restore(recovery) {
      publish({ phase: 'restoring' });
      var current = await adapter.readSnapshot();
      if (current.cart.token !== recovery.cartToken) throw core.fault('rollback-failed');
      var extras = core.cartCodes(current.cart).filter(function (code) {
        return !recovery.added.some(function (added) { return added.toUpperCase() === code.toUpperCase(); });
      });
      var target = core.unique(recovery.codes.concat(extras));
      var result = await adapter.applyCodes(target);
      var snapshot = await adapter.readSnapshot(result);
      var actual = core.cartCodes(snapshot.cart).map(function (code) { return code.toUpperCase(); }).sort();
      if (JSON.stringify(actual) !== JSON.stringify(target.map(function (code) { return code.toUpperCase(); }).sort())) throw core.fault('rollback-failed');
      var accepted = new Set(snapshot.cart.discount_codes.filter(function (entry) { return entry.applicable; }).map(function (entry) { return entry.code.toUpperCase(); }));
      if (recovery.accepted.some(function (code) { return !accepted.has(code.toUpperCase()); })) throw core.fault('rollback-failed');
      save({ cartToken: snapshot.cart.token, owned: recovery.ownedBefore });
      adapter.render(snapshot);
      return snapshot;
    }
    async function perform() {
      if (!needsWork()) { publish({ phase: 'idle' }); return true; }
      if (pending) { schedule(); return false; }
      var stamp = version, transaction = null;
      publish({ phase: 'pending' });
      try {
        if (ledger.recovery) {
          try { await restore(ledger.recovery); } catch (error) { throw core.fault('rollback-failed'); }
        }
        var before = await adapter.readSnapshot();
        if (version !== stamp || pending) return false;
        var active = adapter.isActive();
        var owned = ownedFor(before.cart);
        core.coherent(before.cart, before.meta);
        var participating = active && before.meta.items.some(function (item) { return Boolean(core.group(item.tags, adapter.config)); });
        if (!participating && !owned.length) {
          // No BF offer to apply and no owned codes to remove. Keep the native
          // cart and every customer code untouched, even if its response does
          // not expose the discount-code list required for BF reconciliation.
          core.money(before.cart.total_price);
          save({ cartToken: before.cart.token, owned: [] });
          adapter.render(before);
          if (version !== stamp || pending || adapter.isActive() !== active) { schedule(); return false; }
          publish({ phase: active ? 'ready' : 'idle', savings: 0, total: before.cart.total_price, itemCount: before.cart.item_count, cartId: cartIdentity(before.cart.token), required: 0 });
          return true;
        }
        var plan = core.plan(before.cart, before.meta, adapter.config, owned, active);
        var snapshot = before;
        if (plan.changed) {
          transaction = { cartToken: before.cart.token, codes: plan.before,
            accepted: before.cart.discount_codes.filter(function (entry) { return entry.applicable; }).map(function (entry) { return entry.code; }),
            added: plan.target.filter(function (code) { return !plan.before.some(function (old) { return old.toUpperCase() === code.toUpperCase(); }); }),
            ownedBefore: owned };
          // Persist before the write: a network error does not prove the server
          // rejected the write. A later retry can restore the previous list.
          save({ cartToken: before.cart.token, owned: core.unique(owned.concat(plan.owned)), recovery: transaction });
          var result = await adapter.applyCodes(plan.target);
          snapshot = await adapter.readSnapshot(result);
        }
        var confirmed = core.verify(snapshot.cart, snapshot.meta, plan, before.cart);
        save({ cartToken: snapshot.cart.token, owned: plan.owned });
        adapter.render(snapshot);
        if (version !== stamp || pending || adapter.isActive() !== active) { schedule(); return false; }
        publish({ phase: active ? 'ready' : 'idle', savings: confirmed.savings, total: confirmed.total, itemCount: snapshot.cart.item_count, cartId: cartIdentity(snapshot.cart.token), required: plan.required.length });
        return true;
      } catch (error) {
        if (transaction) {
          try { await restore(transaction); } catch (restoreError) { error = core.fault('rollback-failed'); }
        } else if (before) {
          // Even an overflow/configuration error must show the actual latest
          // items so the buyer can resolve it without editing a stale cart.
          try { adapter.render(before); } catch (renderError) { /* Keep checkout blocked. */ }
        }
        publish({ phase: 'error', code: error.code || 'network', details: error.details || {} });
        return false;
      } finally {
        if ((version !== stamp || pending) && needsWork()) schedule();
      }
    }
    function synchronize() {
      if (timer !== null) { clearTimeout(timer); timer = null; }
      if (syncPromise) return syncPromise;
      syncPromise = enqueue(perform).finally(function () { syncPromise = null; });
      return syncPromise;
    }
    return Object.freeze({ request: request, synchronize: synchronize, invalidate: invalidate, needsWork: needsWork,
      getState: function () { return Object.assign({}, state); }, dispose: function () { if (timer !== null) clearTimeout(timer); } });
  }

  function mount(window, document) {
    var config = window.EarlyBFConfig;
    var storageKey = 'early-bf:cart:v1';
    var memoryLedger = null, ledgerWritable = true, rendering = false, approvedCheckout = false, checkoutAttempt = false, latestSnapshot = null;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      (window.T4Sroutes && window.T4Sroutes.root_url) || '/';
    if (!root.endsWith('/')) root += '/';
    var nativeFetch = window.fetch.bind(window);
    var isCartPage = /\/cart\/?$/.test(window.location.pathname);
    var cartSection = isCartPage ? 'main-cart' : 'mini_cart';
    var sectionIds = ['cart_data', cartSection, 'bf-cart-data'];
    function sectionContext() {
      var query = new URLSearchParams(window.location.search || '');
      ['bf_access', 'bf_reset', 'sections', 'section_id', '_bf'].forEach(function (key) { query.delete(key); });
      var theme = window.Shopify && window.Shopify.theme;
      if (!query.has('preview_theme_id') && theme && (theme.role === 'unpublished' || theme.role === 'development')) {
        var themeId = Number(theme.id);
        if (Number.isSafeInteger(themeId) && themeId > 0) query.set('preview_theme_id', String(themeId));
      }
      var search = query.toString();
      return (window.location.pathname || root) + (search ? '?' + search : '');
    }
    function sectionsUrl() {
      var context = sectionContext();
      return context + (context.indexOf('?') === -1 ? '?' : '&') + 'sections=' + sectionIds.join(',') + '&_bf=' + Date.now();
    }
    var identitiesByKey = new Map(), keysByIdentity = new Map();
    function rememberKeys(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      cart.items.forEach(function (item) {
        if (!item.key) return;
        var identity = core.identity(item);
        identitiesByKey.set(item.key, identity);
        keysByIdentity.set(identity, item.key);
      });
    }
    async function mutationFetch(url, options) {
      var prepared = options;
      if (/\/cart\/change\.js(?:\?|$)/.test(url) && options && typeof options.body === 'string') {
        try {
          var body = JSON.parse(options.body);
          var identity = identitiesByKey.get(body.id);
          if (identity && keysByIdentity.has(identity)) {
            body.id = keysByIdentity.get(identity);
            prepared = Object.assign({}, options, { body: JSON.stringify(body) });
          }
        } catch (error) { /* Leave an unrecognized request untouched. */ }
      }
      var response = await nativeFetch(url, prepared);
      try { rememberKeys(await response.clone().json()); } catch (error) { /* Kalles handles malformed responses. */ }
      return response;
    }
    function parseMeta(sections) {
      // Kalles already requests cart_data for every cart mutation. Prefer its
      // embedded metadata; the standalone section remains a legacy fallback.
      var sources = ['cart_data', 'bf-cart-data'], reason = 'metadata-section-missing';
      for (var index = 0; index < sources.length; index++) {
        var html = sections && sections[sources[index]];
        if (typeof html !== 'string') continue;
        var node = new window.DOMParser().parseFromString(html, 'text/html').querySelector('[data-bf-cart-data]');
        if (!node) { reason = 'metadata-node-missing'; continue; }
        try {
          var meta = JSON.parse(node.textContent);
          if (meta && Array.isArray(meta.items)) return meta;
          reason = 'metadata-shape-invalid';
        } catch (error) { reason = 'metadata-json-invalid'; }
      }
      throw core.fault('verification-unavailable', { reason: reason });
    }
    async function json(url, options) {
      var controller = new window.AbortController();
      var timeout = window.setTimeout(function () { controller.abort(); }, 15000);
      try {
        var response = await nativeFetch(url, Object.assign({ credentials: 'same-origin', cache: 'no-store', signal: controller.signal }, options));
        if (!response.ok) throw core.fault('network');
        var data = await response.json();
        if (data.status && data.status >= 400) throw core.fault('network');
        return data;
      } finally { window.clearTimeout(timeout); }
    }
    async function readSnapshot(hint) {
      var cart, sections, meta;
      if (hint && hint.meta) {
        cart = await json(root + 'cart.js');
        try { core.coherent(cart, hint.meta); rememberKeys(cart); return { cart: cart, meta: hint.meta, sections: hint.sections }; } catch (error) { /* Read fresh sections below. */ }
      }
      for (var attempt = 0; attempt < 2; attempt++) {
        var results = await Promise.all([json(root + 'cart.js'), json(sectionsUrl())]);
        cart = results[0]; sections = results[1];
        try { meta = parseMeta(sections); core.coherent(cart, meta); rememberKeys(cart); return { cart: cart, meta: meta, sections: sections }; } catch (error) { if (attempt === 1) throw error; }
      }
    }
    function loadLedger() {
      if (!ledgerWritable) return memoryLedger;
      try {
        var raw = window.localStorage.getItem(storageKey);
        if (raw === null) { memoryLedger = null; return null; }
        var parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.owned) && parsed.owned.every(function (code) { return typeof code === 'string'; })) memoryLedger = parsed;
      } catch (error) { /* Memory fallback never changes other storage keys. */ }
      return memoryLedger;
    }
    function saveLedger(ledger) {
      memoryLedger = ledger;
      try {
        if (!ledger.owned.length && !ledger.recovery) window.localStorage.removeItem(storageKey);
        else window.localStorage.setItem(storageKey, JSON.stringify(ledger));
      } catch (error) { ledgerWritable = false; /* Retain the ledger for this page. */ }
    }
    function render(snapshot) {
      latestSnapshot = snapshot;
      rendering = true;
      try {
        var theme = window.T4SThemeSP;
        if (theme && theme.Cart && snapshot.sections && snapshot.sections.cart_data && snapshot.sections[cartSection]) {
          theme.Cart.renderContents(snapshot.sections, false);
        } else {
          var html = snapshot.sections && snapshot.sections[cartSection];
          if (typeof html !== 'string') throw core.fault('verification-unavailable', { reason: 'cart-section-missing', section: cartSection });
          var parsed = new window.DOMParser().parseFromString(html, 'text/html');
          document.querySelectorAll('[data-cart-wrapper]').forEach(function (form) {
            ['[data-cart-items]', '[data-cart-prices]', '[data-cart-discounts]', '[data-cart-ship-text]'].forEach(function (selector) {
              var from = parsed.querySelector(selector), to = form.querySelector(selector);
              if (from && to) to.innerHTML = from.innerHTML;
            });
            var fromBar = parsed.querySelector('[data-cart-ship-bar]'), toBar = form.querySelector('[data-cart-ship-bar]');
            if (fromBar && toBar) toBar.style.cssText = fromBar.style.cssText;
          });
          document.querySelectorAll('[data-cart-count]').forEach(function (node) { node.textContent = snapshot.cart.item_count; });
          document.querySelectorAll('[data-cart-tt-price]').forEach(function (node) { node.innerHTML = window.EarlyBFPricing.format(snapshot.cart.total_price); });
        }
      } finally { rendering = false; }
    }
    function message(state) {
      if (state.phase === 'ready') return state.required ? config.labels.ready : config.labels.no_promotional_items;
      if (state.phase === 'restoring') return config.labels.restoring;
      if (state.phase !== 'error') return config.labels.pending;
      var key = { 'code-limit': 'limit', 'code-conflict': 'conflict', 'code-rejected': 'rejected',
        'currency-mismatch': 'currency', 'invalid-groups': 'invalid', 'multiple-groups': 'invalid',
        'unknown-group': 'invalid', 'unsupported-product': 'unsupported', 'rollback-failed': 'rollback',
        'verification-unavailable': 'unavailable', 'codes-unavailable': 'unavailable', 'cart-changed': 'unavailable' }[state.code] || 'network';
      return config.labels[key] + (state.details && state.details.code ? ' (' + state.details.code + ')' : '');
    }
    function updateUI(state, needsWork) {
      var active = window.EarlyBF.getState().active;
      var busy = needsWork && state.phase !== 'ready' && state.phase !== 'idle';
      document.documentElement.classList.toggle('bf-syncing', busy);
      document.documentElement.classList.toggle('bf-retiring', !active && needsWork);
      document.querySelectorAll('[data-cart-wrapper]').forEach(function (form) { form.setAttribute('aria-busy', state.phase === 'pending' || state.phase === 'restoring' ? 'true' : 'false'); });
      document.querySelectorAll('[data-bf-cart-status]').forEach(function (panel) {
        panel.hidden = !needsWork || state.phase === 'idle' || (state.phase === 'ready' && !state.required);
        panel.setAttribute('data-state', state.phase);
        panel.querySelector('[data-bf-cart-message]').textContent = message(state);
        var savings = panel.querySelector('[data-bf-cart-saving]');
        savings.hidden = state.phase !== 'ready' || !state.savings;
        if (!savings.hidden) savings.innerHTML = window.EarlyBFPricing.escape(config.labels.savings) + ' ' + window.EarlyBFPricing.format(state.savings);
        panel.querySelector('[data-bf-cart-retry]').hidden = state.phase !== 'error';
        panel.querySelector('[data-bf-cart-exit]').hidden = state.phase !== 'error' || !active || (state.code === 'code-limit' && config.overflowPolicy !== 'offer_standard');
      });
      document.querySelectorAll('[data-bf-cart-label]').forEach(function (node) {
        node.hidden = true;
        if (!active || state.phase !== 'ready' || !latestSnapshot) return;
        var item = latestSnapshot.meta.items.find(function (line) { return line.key === node.getAttribute('data-bf-line-key'); });
        if (!item) return;
        var group = core.group(item.tags, config);
        if (!group) return;
        var saving = item.allocations.filter(function (allocation) {
          return allocation.type === 'discount_code' && allocation.title.toUpperCase() === group.code.toUpperCase();
        }).reduce(function (sum, allocation) { return sum + allocation.amount; }, 0);
        if (!saving || !item.originalPrice) return;
        var percent = core.percent(core.reference(item.originalPrice, item.compareAt) * item.quantity, item.finalLinePrice);
        node.innerHTML = '<span class="bf-label">' + window.EarlyBFPricing.escape(config.badgeLabel) + '</span><span class="bf-percent">−' + percent + '%</span>';
        node.hidden = false;
      });
      // Kalles stores a separate checkout-form discount value. During BF that
      // value must mirror the server list and must never overwrite it on submit.
      if (needsWork) document.querySelectorAll('input[name="discount"]').forEach(function (input) { input.disabled = true; input.setAttribute('data-bf-disabled-coupon', ''); });
      else document.querySelectorAll('[data-bf-disabled-coupon]').forEach(function (input) { input.disabled = false; input.removeAttribute('data-bf-disabled-coupon'); });
      document.dispatchEvent(new window.CustomEvent('bf:cart-state', { detail: Object.assign({}, state) }));
    }
    var controller = createController({
      config: config, isActive: function () { return window.EarlyBF.getState().active; },
      fetch: mutationFetch, nativeFetch: nativeFetch, loadLedger: loadLedger, saveLedger: saveLedger, readSnapshot: readSnapshot,
      applyCodes: async function (codes) {
        var cart = await json(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: sectionIds, sections_url: sectionContext() }) });
        var meta = null;
        try { meta = parseMeta(cart.sections); } catch (error) { /* Section failure requires a fresh read; never fabricate data. */ }
        return { cart: cart, sections: cart.sections, meta: meta };
      }, render: render, onState: updateUI
    });
    function refreshUI() { updateUI(controller.getState(), controller.needsWork()); }
    // Direct hooks cover Kalles render/init. Its cart:updated event is also
    // emitted for quick views and errors, so it is not a mutation signal.
    function afterThemeRender() { if (!rendering) controller.invalidate(); refreshUI(); }
    document.addEventListener('bf:access-changed', function () { controller.invalidate(); refreshUI(); });
    window.addEventListener('pageshow', function () { controller.invalidate(); });
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') controller.invalidate(); });
    document.addEventListener('click', function (event) {
      var target = event.target.closest && event.target.closest('[data-bf-cart-retry], [data-bf-cart-exit], [data-action="save-discountcode"]');
      if (!target) return;
      if (target.matches('[data-bf-cart-retry]')) { event.preventDefault(); controller.synchronize(); }
      else if (target.matches('[data-bf-cart-exit]')) { event.preventDefault(); window.EarlyBF.deactivate(); controller.synchronize(); }
      else if (controller.needsWork()) { event.preventDefault(); event.stopImmediatePropagation(); controller.invalidate(); }
    }, true);
    function isCheckout(element) {
      return element && element.closest && element.closest('[name="checkout"], [name="goto_pp"], [name="goto_gc"], a[href*="/checkout"], .shopify-payment-button, [data-add-ckt4]');
    }
    async function gate(event, target, form) {
      if (approvedCheckout || !controller.needsWork()) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (checkoutAttempt) return;
      checkoutAttempt = true;
      try {
        if (await controller.synchronize()) {
          if (controller.getState().phase !== 'ready' && controller.needsWork()) return;
          approvedCheckout = true;
          try {
            if (target && target.isConnected) target.click();
            else if (form && form.isConnected) form.requestSubmit();
          } finally { approvedCheckout = false; }
        } else if (window.T4SThemeSP && window.T4SThemeSP.Notices) {
          window.T4SThemeSP.Notices(message(controller.getState()));
        }
      } finally { checkoutAttempt = false; }
    }
    document.addEventListener('click', function (event) {
      var checkout = isCheckout(event.target);
      if (checkout) gate(event, checkout, checkout.closest('form'));
    }, true);
    document.addEventListener('submit', function (event) {
      if (event.target.matches('[data-cart-wrapper]') && (!event.submitter || isCheckout(event.submitter))) gate(event, event.submitter, event.target);
    }, true);
    if (controller.needsWork()) controller.invalidate();
    return Object.freeze({ request: controller.request, synchronize: controller.synchronize,
      getState: controller.getState, afterThemeRender: afterThemeRender });
  }
  return Object.freeze({ createController: createController, mount: mount });
});
